// I am using this code for both website and app
import { type BuildStats, type BuildFit, type Ship } from "@edcc/game-data";

export interface EdsyBuildModule {
  slotName: string;
  moduleSymbol: string | null;
  moduleName: string | null;
  size: number | null;
}

interface EdsyRecord {
  [key: string]: unknown;
  name?: string;
  fdname?: string;
  class?: number;
  mtype?: string;
}

interface EdsyDatabase {
  ship: Record<string, EdsyRecord>;
  module: Record<string, EdsyRecord>;
  mtype: Record<string, EdsyRecord>;
  blueprint: Record<string, EdsyRecord>;
  expeffect: Record<string, EdsyRecord>;
  attributes: Array<{
    attr?: string;
    name?: string;
    unit?: string;
    scale?: number;
    hidden?: number;
  }>;
}

interface EdsySlot {
  getModule(): EdsyRecord | null;
  getEffectiveAttrValue(attribute: string): number;
  setModuleID(id: number): boolean;
  setBlueprint(id: string, grade: number, roll: number): boolean;
  setExpeffectID(id: string): boolean;
}

interface EdsyBuild {
  stats: Record<string, unknown> | null;
  getSlot(group: string, index: number | string): EdsySlot | undefined;
  getStat(key: string): unknown;
  getPowerDist(distributor: "sys" | "eng" | "wep"): number;
  getEffectivePowerDist(distributor: "sys" | "eng" | "wep"): number;
  getCrewDist(): { sys: number; eng: number; wep: number };
}

interface EdsyRuntime {
  Build: new (shipId: number, stock: boolean) => EdsyBuild;
}

declare global {
  interface Window {
    eddb?: EdsyDatabase;
    edsy?: EdsyRuntime;
  }
}

export interface CalculatorMetric {
  label: string;
  value: string;
  cells?: string[];
}

export interface CalculatorMetricGroup {
  title: string;
  columns?: string[];
  rows: CalculatorMetric[];
}

export interface ThermalLoadMetric {
  label: string;
  value: string;
  percentage: number | null;
  status: "steady" | "rising" | "unavailable";
  detail?: string;
}

export interface CalculatedModuleFact {
  label: string;
  value: string;
}

export interface EdsyCalculation {
  stats: Record<string, unknown>;
  buildStats: BuildStats;
  groups: CalculatorMetricGroup[];
  moduleFactsBySlot: Record<string, CalculatedModuleFact[]>;
  thermalLoads: ThermalLoadMetric[];
}

export type EdsyCalculationResult =
  | { status: "ready"; calculation: EdsyCalculation }
  | { status: "unavailable"; reason: string; unsupportedModules: string[]; moduleFactsBySlot?: Record<string, CalculatedModuleFact[]> };

let runtimePromise: Promise<{ runtime: EdsyRuntime; database: EdsyDatabase }> | null = null;

function normalize(value: string | null | undefined): string {
  return (value ?? "").toLowerCase().replace(/[^a-z0-9]/g, "");
}

function appendScript(path: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = path;
    script.async = false;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error(`Could not load calculator file: ${path}`));
    document.head.append(script);
  });
}

async function loadRuntime(): Promise<{ runtime: EdsyRuntime; database: EdsyDatabase }> {
  if (window.edsy && window.eddb) {
    return { runtime: window.edsy, database: window.eddb };
  }
  if (!runtimePromise) {
    runtimePromise = (async () => {
      await appendScript("/vendor/edcc-calculator/eddb.js");
      await appendScript("/vendor/edcc-calculator/edsy.js");
      if (!window.edsy || !window.eddb) {
        throw new Error("The outfitting calculator loaded without its database.");
      }
      return { runtime: window.edsy, database: window.eddb };
    })().catch((error: unknown) => {
      runtimePromise = null;
      throw error;
    });
  }
  return runtimePromise;
}

function findShipId(ship: Ship, database: EdsyDatabase): number | null {
  const symbol = normalize(ship.fdSymbol);
  const name = normalize(ship.name);
  for (const [id, record] of Object.entries(database.ship)) {
    if (
      (symbol && normalize(record.fdname) === symbol) ||
      (name && normalize(record.name) === name)
    ) {
      return Number(id);
    }
  }
  return null;
}

function findModuleId(
  module: EdsyBuildModule,
  shipId: number,
  database: EdsyDatabase,
): number | null {
  const symbol = normalize(module.moduleSymbol);
  if (symbol) {
    const shipModules = database.ship[String(shipId)] as
      | (EdsyRecord & { module?: Record<string, EdsyRecord> })
      | undefined;
    for (const [id, record] of Object.entries(shipModules?.module ?? {})) {
      if (normalize(record.fdname) === symbol) return Number(id);
    }
    for (const [id, record] of Object.entries(database.module)) {
      if (normalize(record.fdname) === symbol) return Number(id);
    }
    return null;
  }

  const name = normalize(module.moduleName);
  if (!name) return null;
  const matches = Object.entries(database.module).filter(([, record]) =>
    normalize(record.name) === name && record.class === module.size
  );
  return matches.length === 1 ? Number(matches[0]?.[0]) : null;
}

function slotTarget(
  ship: Ship,
  module: EdsyBuildModule,
): { group: string; index: number } | null {
  const slot = ship.buildSlots.find((candidate) => candidate.slotName === module.slotName);
  if (!slot) return null;
  if (slot.sourceGroup !== undefined && slot.sourceIndex !== undefined) return { group: slot.sourceGroup, index: slot.sourceIndex };
  if (slot.kind === "core") return { group: "component", index: slot.index + 1 };
  if (slot.kind === "bulkhead") return { group: "component", index: 0 };
  if (slot.kind === "hardpoint") return { group: "hardpoint", index: slot.index };
  if (slot.kind === "utility") return { group: "utility", index: slot.index };
  if (slot.kind !== "optional") return null;
  if (slot.slotName === "PlanetaryApproachSuite") return null;

  const isMilitary = slot.slotName.toLowerCase().startsWith("military");
  const preceding = ship.buildSlots.filter((candidate) => {
    if (candidate.kind !== "optional" || candidate.slotName === "PlanetaryApproachSuite") return false;
    if (candidate.index >= slot.index) return false;
    return candidate.slotName.toLowerCase().startsWith("military") === isMilitary;
  }).length;
  return { group: isMilitary ? "military" : "internal", index: preceding };
}

function findBlueprintId(
  slot: EdsySlot,
  fdName: string | null,
  displayName: string | null,
  database: EdsyDatabase,
): string | null {
  const moduleRecord = slot.getModule();
  const moduleType = moduleRecord?.mtype;
  if (!moduleType) return null;
  const availableBlueprints = database.mtype[moduleType]?.blueprints;
  if (!Array.isArray(availableBlueprints)) return null;
  const wantedFdName = normalize(fdName);
  const wantedName = normalize(displayName);
  for (const candidate of availableBlueprints) {
    const record = database.blueprint[String(candidate)];
    if (
      record &&
      ((wantedFdName && normalize(typeof record.fdname === "string" ? record.fdname : null) === wantedFdName) ||
        (wantedName && normalize(typeof record.name === "string" ? record.name : null) === wantedName))
    ) {
      return String(candidate);
    }
  }
  return null;
}

function findExperimentalId(value: string, database: EdsyDatabase): string | null {
  const wanted = normalize(value);
  for (const [id, record] of Object.entries(database.expeffect)) {
    if (normalize(record.fdname) === wanted || normalize(record.name) === wanted) return id;
  }
  return null;
}

function numberValue(stats: Record<string, unknown>, key: string): number | null {
  const value = stats[key];
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function formatted(value: number | null, unit = "", scale = 2): string {
  if (value === null) return "Not available";
  return `${value.toLocaleString("en-US", { maximumFractionDigits: scale })}${unit ? ` ${unit}` : ""}`;
}

function metric(
  stats: Record<string, unknown>,
  key: string,
  label: string,
  unit = "",
  scale = 2,
): CalculatorMetric | null {
  const value = numberValue(stats, key);
  return value === null ? null : { label, value: formatted(value, unit, scale) };
}

function percentageMetric(
  stats: Record<string, unknown>,
  key: string,
  label: string,
): CalculatorMetric | null {
  const value = numberValue(stats, key);
  return value === null ? null : { label, value: formatted(value * 100, "%", 1) };
}

function metricGroup(title: string, rows: Array<CalculatorMetric | null>): CalculatorMetricGroup {
  return { title, rows: rows.filter((row): row is CalculatorMetric => row !== null) };
}

export function createCalculatorMetricGroups(stats: Record<string, unknown>): CalculatorMetricGroup[] {
  return [
    metricGroup("Load and cost", [
      metric(stats, "cost", "Build cost", "cr", 0),
      metric(stats, "cost_rearm", "Rearm", "cr", 0),
      metric(stats, "cost_restock", "Restock", "cr", 0),
      metric(stats, "cost_vehicle", "Vehicle replacement", "cr", 0),
      metric(stats, "mass_hull", "Hull mass", "t"),
      metric(stats, "mass_unladen", "Unladen mass", "t"),
      metric(stats, "mass_laden", "Laden mass", "t"),
      metric(stats, "fuelcap", "Fuel capacity", "t"),
      metric(stats, "cargocap", "Cargo capacity", "t"),
      metric(stats, "cabincap", "Passenger capacity", "seats", 0),
      metric(stats, "scooprate", "Fuel scoop rate", "t/s", 3),
    ]),
    metricGroup("Movement and range", [
      metric(stats, "jumpbst", "Jump range bonus", "ly"),
      metric(stats, "_jump_max", "Maximum jump", "ly"),
      metric(stats, "_jump_unladen", "Unladen jump", "ly"),
      metric(stats, "_jump_laden", "Laden jump", "ly"),
      metric(stats, "_range_unladen", "Unladen tank range", "ly"),
      metric(stats, "_range_laden", "Laden tank range", "ly"),
      metric(stats, "_speed", "Speed", "m/s", 0),
      metric(stats, "_boost", "Boost speed", "m/s", 0),
    ]),
    metricGroup("Power", [
      metric(stats, "pwrcap", "Power plant output", "MW"),
      metric(stats, "pwrbst", "Power plant boost", "MW"),
      metric(stats, "pwrdraw_dep_total", "Deployed draw", "MW"),
      metric(stats, "pwrdraw_ret_total", "Retracted draw", "MW"),
    ]),
    metricGroup("Defence", [
      metric(stats, "_armour", "Hull strength"),
      metric(stats, "hullbst", "Hull boost", "%", 1),
      metric(stats, "hullrnf", "Hull reinforcement"),
      metric(stats, "_akinres", "Kinetic resistance", "%"),
      metric(stats, "_athmres", "Thermal resistance", "%"),
      metric(stats, "_aexpres", "Explosive resistance", "%"),
      metric(stats, "_acaures", "Caustic resistance", "%"),
      metric(stats, "_shields", "Shield strength", "MJ"),
      metric(stats, "shieldbst", "Shield boost", "%", 1),
      metric(stats, "shieldrnf", "Shield reinforcement"),
      metric(stats, "shieldrnfps", "Shield cell bank reinforcement rate", "/s", 1),
      metric(stats, "_skinres", "Shield kinetic resistance", "%"),
      metric(stats, "_sthmres", "Shield thermal resistance", "%"),
      metric(stats, "_sexpres", "Shield explosive resistance", "%"),
      metric(stats, "_scaures", "Shield caustic resistance", "%"),
    ]),
    metricGroup("Weapons", [
      metric(stats, "dps", "Total DPS"),
      metric(stats, "dps_abs", "Absolute DPS"),
      metric(stats, "dps_thm", "Thermal DPS"),
      metric(stats, "dps_kin", "Kinetic DPS"),
      metric(stats, "dps_exp", "Explosive DPS"),
      metric(stats, "dps_axe", "AX damage per second"),
      metric(stats, "dps_cau", "Caustic DPS"),
      metric(stats, "dps_nodistdraw", "DPS without distributor draw"),
      metric(stats, "dps_distdraw", "DPS while drawing from distributor"),
      metric(stats, "thmload_hardpoint_wepfull", "Weapon heat at full capacitor", "/s"),
      metric(stats, "wepcap_burst_cur", "Weapon capacitor burst time", "s"),
      metric(stats, "wepcap_burst_max", "Maximum weapon capacitor burst time", "s"),
      percentageMetric(stats, "wepchg_sustain_cur", "Weapon capacitor sustain"),
      percentageMetric(stats, "wepchg_sustain_max", "Maximum weapon capacitor sustain"),
    ]),
    metricGroup("Thermal load", [
      metric(stats, "thmload_ct", "Thruster heat load", "/s"),
      metric(stats, "thmload_cfsd", "Frame Shift Drive heat load", "/s"),
      metric(stats, "thmload_hardpoint_wepempty", "Weapon heat at empty capacitor", "/s"),
      metric(stats, "thmload_iscb", "Shield Cell Bank heat load", "/s"),
      metric(stats, "spinup_iscb", "Shield Cell Bank spin-up", "s"),
    ]),
  ];
}

interface DetailedStatsContext {
  build: EdsyBuild;
  ship: Ship;
  shipRecord: EdsyRecord;
  thermalLoads: ThermalLoadMetric[];
}

function edsyRow(label: string, ...cells: string[]): CalculatorMetric {
  return { label, value: cells.join(" / "), cells };
}

function edsyTable(
  title: string,
  columns: string[],
  rows: CalculatorMetric[],
): CalculatorMetricGroup {
  return { title, columns, rows };
}

function formatDuration(seconds: number | null): string {
  if (seconds === null || !Number.isFinite(seconds) || seconds < 0) return "N/A";
  if (seconds < 60) return `${seconds.toFixed(seconds < 10 ? 1 : 0)}s`;
  const minutes = Math.floor(seconds / 60);
  const remainder = Math.round(seconds % 60);
  return remainder === 60 ? `${minutes + 1}m` : `${minutes}m ${remainder}s`;
}

function getMassCurveMultiplier(
  mass: number,
  minMass: number,
  optMass: number,
  maxMass: number,
  minMultiplier: number,
  optMultiplier: number,
  maxMultiplier: number,
): number {
  if (maxMass <= minMass || maxMass <= optMass || maxMultiplier === minMultiplier) return Number.NaN;
  const massFraction = Math.min(1, (maxMass - mass) / (maxMass - minMass));
  const exponent = Math.log((optMultiplier - minMultiplier) / (maxMultiplier - minMultiplier)) /
    Math.log((maxMass - optMass) / (maxMass - minMass));
  return minMultiplier + Math.pow(massFraction, exponent) * (maxMultiplier - minMultiplier);
}

function createDetailedStatsGroups(
  stats: Record<string, unknown>,
  context: DetailedStatsContext,
): CalculatorMetricGroup[] {
  const { build, ship, shipRecord, thermalLoads } = context;
  const stat = (key: string) => numberValue(stats, key);
  const attr = (slot: EdsySlot | undefined, key: string) => slot?.getEffectiveAttrValue(key) ?? Number.NaN;
  const format = (amount: number | null, unit = "", digits = 1) =>
    amount === null || !Number.isFinite(amount) ? "N/A" : formatted(amount, unit, digits);
  const hull = build.getSlot("ship", "hull");
  const core = (group: string) => {
    const slot = ship.buildSlots.find((candidate) => candidate.kind === "core" && candidate.group === group);
    return slot ? build.getSlot("component", slot.index + 1) : undefined;
  };
  const fsd = core("fsd");
  const thrusters = core("t");
  const distributor = core("pd");
  const mass = stat("mass") ?? Number.NaN;
  const fuel = stat("fuelcap") ?? 0;
  const cargo = stat("cargocap") ?? 0;
  const reserve = typeof shipRecord.fuelreserve === "number" ? shipRecord.fuelreserve : 0;
  const currentMass = mass + fuel + reserve;
  const ladenMass = mass + fuel + cargo;
  const jumpBonus = stat("jumpbst") ?? 0;
  const maxJumpFuel = attr(fsd, "maxfuel");
  const fsdOptMass = attr(fsd, "fsdoptmass");
  const fsdFuelMul = attr(fsd, "fuelmul");
  const fsdFuelPower = attr(fsd, "fuelpower");
  const jumpDistance = (shipMass: number, jumpFuel: number) => {
    if (
      shipMass <= 0 || jumpFuel <= 0 || fsdOptMass <= 0 ||
      fsdFuelMul <= 0 || fsdFuelPower <= 0
    ) return null;
    return Math.pow(jumpFuel / fsdFuelMul, 1 / fsdFuelPower) * fsdOptMass / shipMass + jumpBonus;
  };
  const currentJump = jumpDistance(currentMass, Math.min(fuel, maxJumpFuel));
  const scoopRate = stat("scooprate") ?? 0;
  const speedCurve = (loadMass: number, kind: "spd" | "rot") =>
    getMassCurveMultiplier(
      loadMass,
      attr(thrusters, "engminmass"),
      attr(thrusters, "engoptmass"),
      attr(thrusters, "engmaxmass"),
      attr(thrusters, kind === "spd" ? "minmulspd" : "minmulrot"),
      attr(thrusters, kind === "spd" ? "optmulspd" : "optmulrot"),
      attr(thrusters, kind === "spd" ? "maxmulspd" : "maxmulrot"),
    ) / 100;
  const engPips = build.getEffectivePowerDist("eng");
  const engRatio = engPips / 8;
  const speedAt = (loadMass: number, current = false) => {
    const speed = attr(hull, "topspd");
    const minThrust = attr(hull, "minthrust") / 100;
    const pipsMultiplier = current ? engRatio + minThrust * (1 - engRatio) : 1;
    const result = speedCurve(loadMass, "spd") * speed * pipsMultiplier;
    return Number.isFinite(result) ? result : null;
  };
  const boostAt = (loadMass: number) => {
    const output = attr(distributor, "engcap");
    const cost = attr(hull, "boostcost");
    if (output <= cost + 0.005) return null;
    const result = speedCurve(loadMass, "spd") * attr(hull, "bstspd");
    return Number.isFinite(result) ? result : null;
  };
  const handlingAt = (loadMass: number, current: boolean) => {
    const curve = speedCurve(loadMass, "rot");
    const pipRatio = current ? engRatio : 1;
    const rotation = (speedKey: string, minimumKey: string) => {
      const base = attr(hull, speedKey);
      const minimum = attr(hull, minimumKey);
      const speed = curve * (base * pipRatio + minimum * (1 - pipRatio));
      return Number.isFinite(speed) && speed > 0
        ? { speed: format(speed, "", 2), time: formatDuration(180 / speed) }
        : { speed: "ERR", time: "ERR" };
    };
    return {
      pitch: rotation("pitch", "minpitch"),
      roll: rotation("roll", "minroll"),
      yaw: rotation("yaw", "minyaw"),
    };
  };
  const currentHandling = handlingAt(currentMass, true);
  const maxHandling = handlingAt(mass, false);
  const boostCost = attr(hull, "boostcost");
  const engCharge = attr(distributor, "engchg");
  const canBoost = attr(distributor, "engcap") > boostCost + 0.005;
  const boostFrequency = canBoost && engCharge > 0
    ? boostCost / (engCharge * Math.pow(engRatio, 1.1))
    : null;
  const maxBoostFrequency = canBoost && engCharge > 0 ? boostCost / engCharge : null;
  const sysPips = build.getEffectivePowerDist("sys");
  const absShieldRes = 60 * Math.pow(sysPips / 8, 0.85) / 100;
  const rawShield = stat("_shields");
  const shieldGenerator = ship.buildSlots
    .filter((slot) => slot.kind === "optional")
    .map((definition) => {
      const target = slotTarget(ship, {
        slotName: definition.slotName,
        moduleSymbol: null,
        moduleName: null,
        size: null,
      });
      return target ? build.getSlot(target.group, target.index) : undefined;
    })
    .find((slot) => slot?.getModule()?.mtype === "isg");
  const hasActiveShield = Boolean(shieldGenerator && rawShield !== null && rawShield > 0);
  const shieldResist = (key: string) => {
    const resistance = stat(key);
    return !hasActiveShield || resistance === null ? null : resistance / 100;
  };
  const effectiveShieldResist = (key: string) => {
    const resistance = shieldResist(key);
    return resistance === null ? null : 1 - (1 - absShieldRes) * (1 - resistance);
  };
  const shieldStrength = (resistance: number | null) =>
    rawShield === null || rawShield <= 0 || resistance === null || resistance >= 1
      ? "N/A"
      : format(rawShield / (1 - resistance), "", 1);
  const shieldReinforcement = (resistance: number | null) => {
    const amount = stat("shieldrnfps_ammomax");
    return amount === null || amount <= 0 || resistance === null || resistance >= 1
      ? "N/A"
      : format(amount / (1 - resistance), "", 1);
  };
  const shieldTiming = (isBuildTime: boolean) => {
    if (!shieldGenerator || rawShield === null || rawShield <= 0) return "N/A";
    const sysCapacity = attr(distributor, "syscap");
    const sysCharge = attr(distributor, "syschg") * Math.pow(sysPips / 8, 1.1);
    const generatorPower = attr(shieldGenerator, "genpwr");
    const rate = attr(shieldGenerator, isBuildTime ? "bgenrate" : "genrate");
    if (
      sysCapacity <= 0 || sysCharge <= 0 || generatorPower <= 0 || rate <= 0 ||
      ![sysCapacity, sysCharge, generatorPower, rate].every(Number.isFinite)
    ) return "N/A";
    if (!isBuildTime) {
      return formatDuration((rawShield / 2) / Math.min(rate, sysCharge / generatorPower));
    }
    const fast = Math.min(
      rawShield / 2 / rate,
      sysCapacity / Math.max(0, rate * generatorPower - sysCharge),
    );
    const slowRate = Math.min(rate, sysCharge / generatorPower);
    const slow = (rawShield / 2 - rate * fast) / slowRate;
    return formatDuration(16 + fast + slow);
  };
  const dps = stat("dps") ?? 0;
  const damageShare = (key: string) =>
    dps > 0 ? format((stat(key) ?? 0) / dps * 100, "%", 0) : "N/A";
  const currentSustain = dps > 0
    ? ((stat("dps_nodistdraw") ?? 0) +
      (stat("dps_distdraw") ?? 0) * (stat("wepchg_sustain_cur") ?? 0)) / dps
    : null;
  const maxSustain = dps > 0
    ? ((stat("dps_nodistdraw") ?? 0) +
      (stat("dps_distdraw") ?? 0) * (stat("wepchg_sustain_max") ?? 0)) / dps
    : null;
  const ammoWithPower = stat("ammotime_wepcap");
  const ammoWithoutPower = stat("ammotime_nocap");
  const burstMax = stat("wepcap_burst_max");
  const ammoDuration = maxSustain && ammoWithPower !== null && burstMax !== null
    ? Math.min(
      ammoWithoutPower ?? Number.POSITIVE_INFINITY,
      ammoWithPower <= burstMax
        ? ammoWithPower
        : burstMax + (ammoWithPower - burstMax) / maxSustain,
    )
    : null;
  const thermalValue = (label: string) =>
    thermalLoads.find((load) => load.label === label)?.value ?? "N/A";
  const cost = (key: string) => format(stat(key), "", 0);
  const fuelCost = typeof shipRecord.fuelcost === "number"
    ? shipRecord.fuelcost * (stat("fuelcap") ?? 0)
    : null;
  const powerPips = (key: "sys" | "eng" | "wep") => build.getPowerDist(key);
  const crew = build.getCrewDist();
  const armour = stat("_armour");
  const armourIntegrity = (key: string) => {
    const resistance = stat(key);
    return armour === null || resistance === null || resistance >= 100
      ? "N/A"
      : format(armour / (1 - resistance / 100), "", 1);
  };

  return [
    edsyTable("TTL", ["MASS", "FUEL", "CRGO", "PSGR"], [
      edsyRow("CUR", format(currentMass, "", 1), format(fuel, "", 0), "0", "—"),
      edsyRow("MAX", format(mass + fuel + cargo, "", 1), format(fuel, "", 0), format(cargo, "", 0), format(stat("cabincap"), "", 0)),
      edsyRow("CRW", `AVL ${Math.max(0, (attr(hull, "crew") || 1) - 1 - crew.sys - crew.eng - crew.wep)}`, `SYS ${crew.sys}`, `ENG ${crew.eng}`, `WEP ${crew.wep}`),
      edsyRow("DST", `RST`, `SYS ${powerPips("sys")}`, `ENG ${powerPips("eng")}`, `WEP ${powerPips("wep")}`),
    ]),
    edsyTable("NAV", ["JMP", "RNG", "SPD", "BST", "FRQ"], [
      edsyRow("CUR", format(currentJump, "", 2), format(stat("_range_unladen"), "", 2), format(speedAt(currentMass, true), "", 0), format(boostAt(currentMass), "", 0), formatDuration(boostFrequency)),
      edsyRow("LDN", format(stat("_jump_laden"), "", 2), format(stat("_range_laden"), "", 2), format(speedAt(ladenMass), "", 0), format(boostAt(ladenMass), "", 0), "—"),
      edsyRow("UNL", format(stat("_jump_unladen"), "", 2), format(stat("_range_unladen"), "", 2), format(speedAt(mass + fuel), "", 0), format(boostAt(mass + fuel), "", 0), "—"),
      edsyRow("MAX", format(stat("_jump_max"), "", 2), "—", format(speedAt(mass), "", 0), format(boostAt(mass), "", 0), formatDuration(maxBoostFrequency)),
      edsyRow("SCP", formatDuration(scoopRate > 0 ? Math.min(fuel, attr(fsd, "maxfuel")) / scoopRate : null), formatDuration(scoopRate > 0 ? fuel / scoopRate : null), format(scoopRate || null, "", 3), "—", "—"),
    ]),
    edsyTable("HND", ["PIT", "ROL", "YAW"], [
      edsyRow("CUR", currentHandling.pitch.speed, currentHandling.roll.speed, currentHandling.yaw.speed),
      edsyRow("180°", currentHandling.pitch.time, currentHandling.roll.time, currentHandling.yaw.time),
      edsyRow("MAX", maxHandling.pitch.speed, maxHandling.roll.speed, maxHandling.yaw.speed),
      edsyRow("180°", maxHandling.pitch.time, maxHandling.roll.time, maxHandling.yaw.time),
      edsyRow("BST", "N/A", "N/A", "N/A"),
    ]),
    edsyTable("THM", ["LVL"], [
      edsyRow("IDL", thermalValue("Idle")),
      edsyRow("THR", thermalValue("Thrusters")),
      edsyRow("FSD", thermalValue("Frame shift drive")),
      edsyRow("WEP", thermalValue("Weapons · full capacitor")),
      edsyRow("", thermalValue("Weapons · empty capacitor")),
      edsyRow("SCB", thermalValue("Shield cell bank")),
    ]),
    edsyTable("SHD", ["RES", "STR", "RNF"], [
      edsyRow("RAW", "—", hasActiveShield ? format(rawShield, "", 1) : "N/A", hasActiveShield ? format(stat("shieldrnfps_ammomax"), "", 1) : "N/A"),
      edsyRow("ABS", hasActiveShield ? format(absShieldRes * 100, "%", 1) : "N/A", shieldStrength(hasActiveShield ? absShieldRes : null), shieldReinforcement(hasActiveShield ? absShieldRes : null)),
      ...(["KIN", "THM", "EXP"] as const).map((label) => {
        const key = label === "KIN" ? "_skinres" : label === "THM" ? "_sthmres" : "_sexpres";
        const resist = effectiveShieldResist(key);
        return edsyRow(label, format(resist === null ? null : resist * 100, "%", 1), shieldStrength(resist), shieldReinforcement(resist));
      }),
      edsyRow("BLD", "—", shieldTiming(true), "—"),
      edsyRow("RGN", "—", shieldTiming(false), "—"),
    ]),
    edsyTable("ARM", ["RES", "INT"], [
      edsyRow("RAW", format(attr(hull, "hardness"), "", 0), format(armour, "", 1)),
      edsyRow(
        "MRP",
        stat("integ_imrp") ? format((1 - (stat("dmgprot") ?? 1)) * 100, "%", 1) : "N/A",
        format(stat("integ_imrp"), "", 1),
      ),
      ...(["KIN", "THM", "EXP", "CAU"] as const).map((label) => {
        const key = label === "KIN" ? "_akinres"
          : label === "THM" ? "_athmres"
            : label === "EXP" ? "_aexpres" : "_acaures";
        return edsyRow(label, format(stat(key), "%", 1), armourIntegrity(key));
      }),
    ]),
    edsyTable("WPN", ["BST", "DUR"], [
      edsyRow("RAW", format(dps || null, "", 1), formatDuration(stat("wepcap_burst_cur"))),
      edsyRow("ABS", damageShare("dps_abs"), formatDuration(stat("wepcap_burst_max"))),
      edsyRow("KIN", damageShare("dps_kin"), formatDuration(ammoDuration)),
      edsyRow("THM", damageShare("dps_thm"), format(currentSustain === null ? null : currentSustain * 100, "%", 1)),
      edsyRow("EXP", damageShare("dps_exp"), format(maxSustain === null ? null : maxSustain * 100, "%", 1)),
      edsyRow("A-X", damageShare("dps_axe"), "—"),
      edsyRow("CAU", damageShare("dps_cau"), "—"),
    ]),
    edsyTable("PRC", ["COST"], [
      edsyRow("RFL", format(fuelCost, "", 0)),
      edsyRow("RPL", cost("cost_vehicle")),
      edsyRow("RSK", cost("cost_restock")),
      edsyRow("RAR", cost("cost_rearm")),
      edsyRow("RBY", format(stat("cost") === null ? null : (stat("cost") ?? 0) * 0.05, "", 0)),
      edsyRow("BUY", cost("cost")),
    ]),
  ];
}

function moduleFacts(
  slot: EdsySlot,
  database: EdsyDatabase,
): CalculatedModuleFact[] {
  const module = slot.getModule();
  if (!module) return [];
  const attributes = new Map(
    database.attributes
      .filter((attribute) => attribute.attr && attribute.name && !attribute.hidden)
      .map((attribute) => [attribute.attr!, attribute]),
  );
  const type = database.mtype[module.mtype ?? ""];
  const keys = new Set([
    ...Object.keys(module),
    ...(Array.isArray(type?.modifiable) ? type.modifiable.filter((key): key is string => typeof key === "string") : []),
    ...(Array.isArray(type?.keyattrs) ? type.keyattrs.filter((key): key is string => typeof key === "string") : []),
  ]);
  return [...keys]
    .filter(key => attributes.has(key) && Number.isFinite(slot.getEffectiveAttrValue(key)))
    .map(key => {
      const attribute = attributes.get(key);
      const value = slot.getEffectiveAttrValue(key);
      const scale = typeof attribute?.scale === "number" ? attribute.scale : 2;
      const unit = (attribute?.unit ?? "").replaceAll("&deg;", "°");
      return {
        label: attribute?.name ?? key,
        value: formatted(value, unit, scale),
      };
    });
}

function mapBuildStats(
  ship: Ship,
  moduleCount: number,
  stats: Record<string, unknown>,
): BuildStats {
  const deployedDraw = Array.isArray(stats.pwrdraw_dep) ? stats.pwrdraw_dep[0] : null;
  const retractedDraw = Array.isArray(stats.pwrdraw_ret) ? stats.pwrdraw_ret[0] : null;
  const resistance = (key: string) => {
    const value = numberValue(stats, key);
    return value === null ? null : value / 100;
  };
  const hullMass = numberValue(stats, "mass_hull") ?? ship.hullMass ?? 0;
  const unladenMass = numberValue(stats, "mass_unladen") ?? hullMass;
  const ladenMass = numberValue(stats, "mass_laden") ?? unladenMass;
  const shield = numberValue(stats, "_shields");
  return {
    moduleCount,
    price: numberValue(stats, "cost") ?? ship.hullCost ?? 0,
    unladenMass,
    ladenMass,
    currentMass: unladenMass,
    maxMass: ladenMass,
    cargoCapacity: numberValue(stats, "cargocap") ?? 0,
    cargoLoad: 0,
    fuelCapacity: numberValue(stats, "fuelcap") ?? 0,
    fuelLoad: numberValue(stats, "fuelcap") ?? 0,
    fuelReserve: ship.reserveFuelCapacity ?? 0,
    jumpMax: numberValue(stats, "_jump_max"),
    jumpUnladen: numberValue(stats, "_jump_unladen"),
    jumpLaden: numberValue(stats, "_jump_laden"),
    rangeUnladen: numberValue(stats, "_range_unladen"),
    rangeLaden: numberValue(stats, "_range_laden"),
    speed: numberValue(stats, "_speed"),
    boost: numberValue(stats, "_boost"),
    shield: shield !== null && shield > 0 ? shield : null,
    armour: numberValue(stats, "_armour"),
    armourKineticResistance: resistance("_akinres"),
    armourThermalResistance: resistance("_athmres"),
    armourExplosiveResistance: resistance("_aexpres"),
    armourCausticResistance: resistance("_acaures"),
    powerCapacity: numberValue(stats, "pwrcap"),
    powerDraw: typeof deployedDraw === "number" && Number.isFinite(deployedDraw) ? deployedDraw : null,
    powerRetracted:
      typeof retractedDraw === "number" && Number.isFinite(retractedDraw) ? retractedDraw : null,
    warnings: [],
  };
}

function buildUnsupported(
  reason: string,
  unsupportedModules: string[] = [],
  moduleFactsBySlot?: Record<string, CalculatedModuleFact[]>,
): EdsyCalculationResult {
  return { status: "unavailable", reason, unsupportedModules, moduleFactsBySlot };
}

async function calculate(
  ship: Ship,
  modules: EdsyBuildModule[],
  fits: BuildFit[],
): Promise<EdsyCalculationResult> {
  const { runtime, database } = await loadRuntime();
  const shipId = findShipId(ship, database);
  if (shipId === null) {
    return buildUnsupported(
      "This ship is not in the supplied EDSY calculator data. Available stats below use EDCC's data; unsupported readings are omitted.",
    );
  }

  const coreSlots = ship.buildSlots.filter((slot) => slot.kind === "core");
  const fittedCore = new Set(modules.filter((module) => module.moduleSymbol || module.moduleName).map((module) => module.slotName));
  const incompleteCore = coreSlots.some((slot) => !fittedCore.has(slot.slotName));

  const build = new runtime.Build(shipId, false);
  const fitBySlot = new Map(fits.map((fit) => [fit.slot.slotName, fit]));
  const moduleFactsBySlot: Record<string, CalculatedModuleFact[]> = {};
  const unsupportedModules: string[] = [];

  for (const module of modules) {
    if (!module.moduleSymbol && !module.moduleName) continue;
    const target = slotTarget(ship, module);
    if (!target) {
      if (module.slotName !== "PlanetaryApproachSuite") {
        unsupportedModules.push(module.moduleName ?? module.slotName);
      }
      continue;
    }
    const moduleId = findModuleId(module, shipId, database);
    const slot = build.getSlot(target.group, target.index);
    if (moduleId === null || !slot?.setModuleID(moduleId)) {
      unsupportedModules.push(module.moduleName ?? module.moduleSymbol ?? module.slotName);
      continue;
    }

    const fit = fitBySlot.get(module.slotName);
    const engineering = fit?.engineering;
    if (engineering?.grade && (engineering.blueprintFdName || engineering.blueprintName)) {
      const blueprintId = findBlueprintId(
        slot,
        engineering.blueprintFdName,
        engineering.blueprintName,
        database,
      );
      if (blueprintId === null || !slot.setBlueprint(blueprintId, engineering.grade, 1)) {
        unsupportedModules.push(`${module.moduleName ?? module.slotName} (engineering)`);
        continue;
      }
    }
    const effectName = fit?.experimentalKey;
    if (effectName) {
      const effectId = findExperimentalId(effectName, database);
      if (effectId === null || !slot.setExpeffectID(effectId)) {
        unsupportedModules.push(`${module.moduleName ?? module.slotName} (experimental effect)`);
        continue;
      }
    }
    moduleFactsBySlot[module.slotName] = moduleFacts(slot, database);
  }

  if (unsupportedModules.length > 0) {
    return buildUnsupported(
      "Some fitted modules or engineering options are not supported by detailed stats yet.",
      [...new Set(unsupportedModules)],
      moduleFactsBySlot,
    );
  }
  if (incompleteCore) return buildUnsupported("Fit every core module to calculate detailed ship stats.", [], moduleFactsBySlot);

  build.getStat("_jump_unladen");
  const calculatedStats = build.stats;
  if (!calculatedStats) {
    return buildUnsupported("The calculator did not produce build statistics.");
  }
  const stats = { ...calculatedStats };
  const deployedDraw = Array.isArray(stats.pwrdraw_dep) ? stats.pwrdraw_dep[0] : null;
  const retractedDraw = Array.isArray(stats.pwrdraw_ret) ? stats.pwrdraw_ret[0] : null;
  stats.pwrdraw_dep_total = deployedDraw;
  stats.pwrdraw_ret_total = retractedDraw;
  const shipRecord = database.ship[String(shipId)];
  if (!shipRecord) {
    return buildUnsupported("The calculator ship record is missing.");
  }
  const thermalLoads = calculateThermalLoads(ship, build, stats);

  return {
    status: "ready",
    calculation: {
      stats,
      buildStats: mapBuildStats(ship, modules.length, stats),
      groups: createDetailedStatsGroups(stats, { build, ship, shipRecord, thermalLoads }),
      moduleFactsBySlot,
      thermalLoads,
    },
  };
}

export function calculateBuildWithEdsY(
  ship: Ship,
  modules: EdsyBuildModule[],
  fits: BuildFit[],
): Promise<EdsyCalculationResult> {
  return calculate(ship, modules, fits);
}

function finiteStat(stats: Record<string, unknown>, key: string): number | null {
  const value = stats[key];
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function numericStatArray(stats: Record<string, unknown>, key: string): number[] | null {
  const value = stats[key];
  if (!Array.isArray(value)) return null;
  const numbers: number[] = [];
  for (const entry of value) {
    if (typeof entry !== "number" || !Number.isFinite(entry)) return null;
    numbers.push(entry);
  }
  return numbers;
}

function powerDrawForHeat(draws: number[], capacity: number): number {
  let draw = draws[0] ?? 0;
  for (let priority = draws.length - 1; priority >= 1 && draw > capacity; priority -= 1) {
    draw -= draws[priority] ?? 0;
  }
  return Math.max(0, draw);
}

function formatHeatTime(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return "N/A";
  if (seconds < 60) return `${seconds.toFixed(1)} s`;
  const minutes = Math.floor(seconds / 60);
  return `${minutes}m ${Math.round(seconds % 60)}s`;
}

function heatLevelAtTime(
  heatCapacity: number,
  heatDissipation: number,
  heatLoad: number,
  initialLevel: number,
  seconds: number,
): number {
  const dissipationRate = heatDissipation / heatCapacity;
  const loadRate = heatLoad / heatCapacity;
  const sqrtDissipationOverLoad = Math.sqrt(dissipationRate / loadRate);
  const sqrtDissipationTimesLoad = Math.sqrt(dissipationRate * loadRate);
  const offset = -Math.atanh(initialLevel * sqrtDissipationOverLoad) / sqrtDissipationTimesLoad;
  return Math.tanh((seconds - offset) * sqrtDissipationTimesLoad) / sqrtDissipationOverLoad;
}

function heatTimeToLevel(
  heatCapacity: number,
  heatDissipation: number,
  heatLoad: number,
  initialLevel: number,
  targetLevel: number,
): number {
  const dissipationRate = heatDissipation / heatCapacity;
  const loadRate = heatLoad / heatCapacity;
  const sqrtDissipationOverLoad = Math.sqrt(dissipationRate / loadRate);
  const sqrtDissipationTimesLoad = Math.sqrt(dissipationRate * loadRate);
  const offset = -Math.atanh(initialLevel * sqrtDissipationOverLoad) / sqrtDissipationTimesLoad;
  return Math.atanh(targetLevel * sqrtDissipationOverLoad) / sqrtDissipationTimesLoad + offset;
}

function thermalMetric(
  label: string,
  heatLoad: number,
  baselineLoad: number,
  heatDissipation: number,
  heatCapacity: number,
  duration?: number,
): ThermalLoadMetric {
  if (
    heatLoad <= 0 ||
    baselineLoad >= heatDissipation ||
    heatDissipation <= 0 ||
    heatCapacity <= 0
  ) {
    return { label, value: "N/A", percentage: null, status: "unavailable" };
  }

  const totalLoad = heatLoad + baselineLoad;
  if (totalLoad <= heatDissipation) {
    const percentage = Math.sqrt(totalLoad / heatDissipation) / 1.5 * 100;
    return { label, value: `${Math.round(percentage)}%`, percentage, status: "steady" };
  }

  const initialLevel = Math.sqrt(baselineLoad / heatDissipation);
  const timeToHundred = heatTimeToLevel(
    heatCapacity,
    heatDissipation,
    totalLoad,
    initialLevel,
    1,
  );
  const timeAboveThreshold = (heatCapacity / 2) / (totalLoad - heatDissipation);
  const timeToOverheat = timeToHundred + timeAboveThreshold;

  if (duration !== undefined && duration > 0 && timeToHundred > duration) {
    const percentage = heatLevelAtTime(
      heatCapacity,
      heatDissipation,
      totalLoad,
      initialLevel,
      duration,
    ) / 1.5 * 100;
    return { label, value: `${Math.round(percentage)}%`, percentage, status: "steady" };
  }

  if (duration !== undefined && duration > 0 && timeToOverheat > duration) {
    const percentage = (2 + (duration - timeToHundred) / timeAboveThreshold) / 3 * 100;
    return { label, value: `${Math.round(percentage)}%`, percentage, status: "rising" };
  }

  if (duration !== undefined && duration > 0) {
    const timeAtPeak = duration - timeToOverheat;
    const peakLevel = 1.5 + (timeAtPeak * (totalLoad - heatDissipation)) / heatCapacity;
    const coolingTime =
      ((peakLevel - 1.5) * heatCapacity) / (heatDissipation - baselineLoad);
    const percentage = (peakLevel / 1.5) * 100;
    return {
      label,
      value: `${formatHeatTime(timeToOverheat)} to overheat`,
      percentage,
      status: "rising",
      detail: `Peak ${Math.round(percentage)}%; above 100% for ${formatHeatTime(timeAtPeak + coolingTime)}.`,
    };
  }

  return {
    label,
    value: `${formatHeatTime(timeToOverheat)} to overheat`,
    percentage: 100,
    status: "rising",
  };
}

function calculateThermalLoads(
  ship: Ship,
  build: EdsyBuild,
  stats: Record<string, unknown>,
): ThermalLoadMetric[] {
  const hull = build.getSlot("ship", "hull");
  const heatCapacity = hull?.getEffectiveAttrValue("heatcap") ?? 0;
  const heatDissipation = hull?.getEffectiveAttrValue("heatdismax") ?? 0;
  const powerPlant = ship.buildSlots.find((slot) => slot.kind === "core" && slot.group === "pp");
  const powerPlantSlot = powerPlant
    ? build.getSlot("component", powerPlant.index + 1)
    : undefined;
  const heatEfficiency = powerPlantSlot?.getEffectiveAttrValue("heateff") ?? 0;
  const powerCapacity = finiteStat(stats, "pwrcap") ?? 0;
  const retractedDraw = numericStatArray(stats, "pwrdraw_ret");
  const deployedDraw = numericStatArray(stats, "pwrdraw_dep");

  if (
    heatCapacity <= 0 ||
    heatDissipation <= 0 ||
    heatEfficiency <= 0 ||
    powerCapacity <= 0 ||
    !retractedDraw ||
    !deployedDraw
  ) {
    return [];
  }

  const idleHeat = powerDrawForHeat(retractedDraw, powerCapacity) * heatEfficiency;
  const deployedHeat = powerDrawForHeat(deployedDraw, powerCapacity) * heatEfficiency;
  const thrusterHeat = finiteStat(stats, "thmload_ct") ?? 0;
  const fsdHeat = finiteStat(stats, "thmload_cfsd") ?? 0;
  const weaponSustain = finiteStat(stats, "wepchg_sustain_cur") ?? 0;
  const weaponHeatFull = (finiteStat(stats, "thmload_hardpoint_wepfull") ?? 0) * weaponSustain;
  const weaponHeatEmpty = (finiteStat(stats, "thmload_hardpoint_wepempty") ?? 0) * weaponSustain;
  const shieldCellHeat = finiteStat(stats, "thmload_iscb") ?? 0;
  const shieldCellSpinup = finiteStat(stats, "spinup_iscb") ?? undefined;

  return [
    thermalMetric("Idle", idleHeat, 0, heatDissipation, heatCapacity),
    thermalMetric("Thrusters", thrusterHeat, idleHeat, heatDissipation, heatCapacity),
    thermalMetric("Frame shift drive", fsdHeat, idleHeat + thrusterHeat, heatDissipation, heatCapacity),
    thermalMetric("Weapons · full capacitor", weaponHeatFull, deployedHeat + thrusterHeat, heatDissipation, heatCapacity),
    thermalMetric("Weapons · empty capacitor", weaponHeatEmpty, deployedHeat + thrusterHeat, heatDissipation, heatCapacity),
    thermalMetric("Shield cell bank", shieldCellHeat, deployedHeat + thrusterHeat, heatDissipation, heatCapacity, shieldCellSpinup),
  ];
}

export function resolveEdsYShipId(ship: Ship, database: EdsyDatabase): number | null {
  return findShipId(ship, database);
}

export function resolveEdsYSlotTarget(
  ship: Ship,
  module: EdsyBuildModule,
): { group: string; index: number } | null {
  return slotTarget(ship, module);
}
